import * as React from 'react';
import GoogleMapReact from 'google-maps-react-markers';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { BBox } from 'geojson';
import { AnyProps, PointFeature } from 'supercluster';
import { useCallback, useEffect } from 'react';
import Config from '../../../../config';
import VehicleCluster from './VehicleCluster';
import './vehicleMarkersStyle.scss';
import VehicleMarker from './VehicleMarker';
import GoogleMapsStyle from './GoogleMapsStyle';
import { IPosition } from '../../../interfaces/map';
import SearchService from '../../../Services/SearchService';
import { getMapSettings } from '../../../Services/UserService';
import WaypointMarker from './WaypointMarker';
import WaypointCluster from './WaypointCluster';
import { DEFAULT_ZOOM, MIN_POINTS_FOR_DISABLED_CLUSTER } from './constants';
import { IVehicle, IVehicles } from '../../Vehicles/types';
import BluecoinMarker from './BluecoinMarker';
import BluecoinCluster from './BluecoinCluster';
import PermissionsService, { PERMISSION_BLUECOINS } from '../../../Services/PermissionsService';
import {
  SuperclusterBluecoinWorker,
  SuperclusterWaypointWorker,
  SuperclusterWorker,
} from '../CreateWorkers';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { selectedVehiclesAtom, vehiclesAtom } from '../../../store/recoil/vehicles';
import { isOnlinePanelOpenAtom } from '../../OnlinePanel/OnlinePanelState';
import { isBluecoinsOpenAtom } from '../Bluecoins/BluecoinState';
import {
  isDrawWaypointAtom,
  isEditModeAtom,
  isWaypointsOpenAtom,
} from '../Waypoints/WaypointState';
import {
  boundsAtom,
  centerAtom,
  filteredVehiclesAtom,
  filterStringAtom,
  isClusterEnabledAtom,
  isRidePlayerOpenAtom,
  minPointsAtom,
  vehicleFocusAtom,
  zoomAtom,
} from '../OnlineMapState';

function GoogleMapLayer(props: {
  googleMap: google.maps.Map | undefined;
  setGoogleMap: React.Dispatch<React.SetStateAction<google.maps.Map | undefined>>;
}): React.JSX.Element {
  const [clusters, setClusters] = React.useState<PointFeature<AnyProps>[]>([]);
  const [onHoverClusterId, setOnHoverClusterId] = React.useState<string>('');
  const vehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const selectedVehicleIds = useRecoilValue<number[]>(selectedVehiclesAtom);
  const [clustersWaypoints, setClustersWaypoints] = React.useState<PointFeature<AnyProps>[]>([]);
  const [clustersBluecoins, setClustersBluecoins] = React.useState<PointFeature<AnyProps>[]>([]);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const isOnlinePanelOpen = useRecoilValue<number | boolean>(isOnlinePanelOpenAtom);
  const isWaypointsOpen = useRecoilValue<boolean>(isWaypointsOpenAtom);
  const isRidePlayerOpen = useRecoilValue<number | boolean>(isRidePlayerOpenAtom);
  const vehicleFocus = useRecoilValue<boolean>(vehicleFocusAtom);
  const setBounds = useSetRecoilState<BBox | undefined>(boundsAtom);
  const setZoom = useSetRecoilState<number>(zoomAtom);
  const center = useRecoilValue<IPosition>(centerAtom);
  const zoomLevel = useRecoilValue<number>(zoomAtom);
  const [isClusterEnabled, setIsClusterEnabled] = useRecoilState<boolean>(isClusterEnabledAtom);
  const [minPoints, setMinPoints] = useRecoilState<number>(minPointsAtom);
  const filterString = useRecoilValue<string>(filterStringAtom);
  const [filteredVehicles, setFilteredVehicles] = useRecoilState<number[]>(filteredVehiclesAtom);
  const isEditMode = useRecoilValue<boolean>(isEditModeAtom);
  const isDrawWaypoint = useRecoilValue<boolean>(isDrawWaypointAtom);
  const isBluecoinsOpen = useRecoilValue<boolean>(isBluecoinsOpenAtom);

  const isMatchedByFilter = useCallback(
    (vehicle: IVehicle): boolean | null => {
      // const tempString = filterString.replace(/[`~!@#$%^&*()_|+=?;:'",<>{}[\]\\/ ]/gi, '');
      // const temptVehicleName =
      //   vehicle?.name.replace(/[`~!@#$%^&*()_|+=?;:'",<>{}[\]\\/ ]/gi, '') || '';
      // const tempDriverName =
      //   vehicle?.driver?.name?.replace(/[`~!@#$%^&*()_|+=?;:'",<>{}[\]\\/ ]/gi, '') || '';
      // const tempLicenceNumber =
      //   vehicle?.licenceNumber.replace(/[`~!@#$%^&*()_|+=?;:'",<>{}[\]\\/ ]/gi, '') || '';
      const tempString = filterString.replace(/\s+/g, ' ').trim();
      const temptVehicleName = vehicle?.name.replace(/\s+/g, ' ').trim() || '';
      const tempDriverName = vehicle?.driver?.name?.replace(/\s+/g, ' ').trim() || '';
      const tempLicenceNumber = vehicle?.licenceNumber.replace(/\s+/g, ' ').trim() || '';

      const regexp = new RegExp(SearchService.removeAccent(tempString.toLowerCase()));
      let filterMatch = false;
      if (
        vehicle &&
        filterString.length > 0 &&
        // vehicle.driver.name &&
        (regexp.test(SearchService.removeAccent(temptVehicleName.toLowerCase())) ||
          regexp.test(SearchService.removeAccent(tempDriverName.toLowerCase())) ||
          regexp.test(SearchService.removeAccent(tempLicenceNumber.toLowerCase())))
      ) {
        filterMatch = true;
      }
      return filterMatch;
    },
    [filterString]
  );

  // If vehicle matches search on online map - add vehicle id to searchedVehicles (see Bounds.tsx)
  useEffect(() => {
    const selectedVehicles = Object.values(vehicles).filter((i) =>
      selectedVehicleIds.includes(i.id)
    );
    const newFilteredVehicles: number[] = [];
    selectedVehicles.forEach((vehicle: IVehicle) => {
      if (vehicle && filterString.length > 0 && isMatchedByFilter(vehicle)) {
        newFilteredVehicles.push(vehicle.id);
      }
    });
    setFilteredVehicles(newFilteredVehicles);
  }, [filterString, isMatchedByFilter, selectedVehicleIds, setFilteredVehicles, vehicles]);

  useEffect(() => {
    SuperclusterWorker.onmessage = (message) => {
      if (message && message.data) {
        setClusters(message.data);
      }
    };
    SuperclusterWaypointWorker.onmessage = (message) => {
      if (message && message.data) {
        setClustersWaypoints(message.data);
      }
    };
    SuperclusterBluecoinWorker.onmessage = (message) => {
      if (message && message.data) {
        setClustersBluecoins(message.data);
      }
    };
    // When returning from another page to map and zoom was not default. Let's reset it.
    if (selectedVehicleIds.length === 0 && zoomLevel !== DEFAULT_ZOOM) {
      setZoom(DEFAULT_ZOOM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (vehicleFocus && isClusterEnabled) {
      setIsClusterEnabled(false);
    } else if ((!isOnlinePanelOpen || !vehicleFocus) && !isClusterEnabled) {
      setIsClusterEnabled(getMapSettings().value.clusters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleFocus, isOnlinePanelOpen]);

  useEffect(() => {
    // If waypoints, ride player or bluecoins are open and clusters are enabled, hide marker clusters
    if (
      (isWaypointsOpen || isRidePlayerOpen || isBluecoinsOpen) &&
      isClusterEnabled &&
      minPoints === Config.onlineMap.superCluster.minPoints
    ) {
      setMinPoints(MIN_POINTS_FOR_DISABLED_CLUSTER);
    }
    // If waypoints, ride player or bluecoins are closed, show marker cluster when they are enabled
    if (
      (!isWaypointsOpen || !isRidePlayerOpen || !isBluecoinsOpen) &&
      isClusterEnabled &&
      minPoints === MIN_POINTS_FOR_DISABLED_CLUSTER
    ) {
      setMinPoints(Config.onlineMap.superCluster.minPoints);
    }

    // If waypoints, ride player or bluecoins are closed and clusters are disabled - hide clusters
    if (
      !isWaypointsOpen &&
      !isRidePlayerOpen &&
      !isBluecoinsOpen &&
      !isClusterEnabled &&
      minPoints !== MIN_POINTS_FOR_DISABLED_CLUSTER
    ) {
      setMinPoints(MIN_POINTS_FOR_DISABLED_CLUSTER);
    }

    // If waypoints, ride player and bluecoins are closed and clusters are enabled - show clusters
    if (
      !isWaypointsOpen &&
      !isRidePlayerOpen &&
      !isBluecoinsOpen &&
      isClusterEnabled &&
      minPoints === MIN_POINTS_FOR_DISABLED_CLUSTER
    ) {
      setMinPoints(Config.onlineMap.superCluster.minPoints);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClusterEnabled, isRidePlayerOpen, isWaypointsOpen, isBluecoinsOpen]);

  const getVehicleZIndex = (vehicleId: number) => {
    if (String(isOnlinePanelOpen) === String(vehicleId)) {
      return 99999;
    }
    if (filterString && filteredVehicles.includes(vehicleId)) {
      return 1;
    }
    return 0;
  };

  const handleOnHover = (value: string) => {
    setOnHoverClusterId(value);
  };

  return (
    <GoogleMapReact
      apiKey={Config.GoogleMaps.apiKey}
      libraries={Config.GoogleMaps.libraries}
      defaultCenter={{ lat: center.lat, lng: center.lng }}
      defaultZoom={zoomLevel}
      loadScriptExternally={true}
      status="ready"
      options={{
        cameraControl: false,
        streetViewControl: true,
        zoomControl: false,
        mapTypeControl: false,
        rotateControl: false,
        fullscreenControl: false,
        fullscreenControlOptions: {
          position: 2, // google.maps.ControlPosition.TOP_CENTER
        },
        streetViewControlOptions: {
          position: 9, // google.maps.ControlPosition.RIGHT_BOTTOM,
        },
        styles: GoogleMapsStyle, // Custom styles for Google Map
        disableDefaultUI: false, // Enable/disable default Google Maps UI
        clickableIcons: false, // Enable/disable clickable icons for POIsxw
        mapTypeId: getMapSettings().value.mapType || 'roadmap',
        scaleControl: true,
      }}
      onGoogleApiLoaded={({ map }) => {
        if (!props.googleMap) {
          props.setGoogleMap(map);
        }
      }}
      onChange={(event: any) => {
        setZoom(event.zoom);
        const sw = event.bounds.getSouthWest();
        const ne = event.bounds.getNorthEast();

        setBounds([sw.lng(), sw.lat(), ne.lng(), ne.lat()]);
      }}
    >
      {isBluecoinsOpen &&
        PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['read'], selectedCustomers) &&
        clustersBluecoins.map((clusterBluecoin: PointFeature<AnyProps>, index: number) => {
          const [longitude, latitude] = clusterBluecoin.geometry.coordinates;
          const {
            cluster: isCluster,
            tooltip,
            expansionZoom,
            // bluecoinsType,
            bluecoinIds,
          } = clusterBluecoin.properties;

          if (isCluster) {
            const clusterIndex: number = index + 1000000;

            return (
              <BluecoinCluster
                key={`cluster-${clusterBluecoin.id}`}
                lat={latitude}
                lng={longitude}
                expansionZoom={expansionZoom}
                googleMap={props.googleMap}
                clusterId={String(clusterBluecoin.id)}
                zIndex={clusterIndex}
                // bluecoinsType={bluecoinsType}
                tooltipItems={tooltip}
              />
            );
          }
          return (
            <BluecoinMarker
              map={props.googleMap}
              key={`bluecoin-marker-${clusterBluecoin.properties.bluecoinIds.join('-')}`}
              // key={`bluecoin-marker-${clusterBluecoin.properties.bluecoinId}`}
              lat={latitude}
              lng={longitude}
              zIndex={clusterBluecoin.properties.bluecoinIds ? 999999 : index + 10000}
              bluecoinIds={bluecoinIds}
            />
          );
        })}
      {isWaypointsOpen &&
        !isDrawWaypoint &&
        !isEditMode &&
        clustersWaypoints.map((clusterWaypoint: PointFeature<AnyProps>, index: number) => {
          const [longitude, latitude] = clusterWaypoint.geometry.coordinates;
          const {
            cluster: isCluster,
            point_count: pointCount,
            // tooltip,
            expansionZoom,
            data,
            waypointsType,
          } = clusterWaypoint.properties;

          if (isCluster) {
            const clusterIndex: number = index + 1000000;

            return (
              <WaypointCluster
                items={pointCount}
                key={`cluster-${clusterWaypoint.id}`}
                lat={latitude}
                lng={longitude}
                expansionZoom={expansionZoom}
                googleMap={props.googleMap}
                clusterId={String(clusterWaypoint.id)}
                zIndex={clusterIndex}
                waypointsType={waypointsType}
              />
            );
          }

          return (
            <WaypointMarker
              key={`waypoint-marker-${clusterWaypoint.properties.waypointId}`}
              lat={latitude}
              lng={longitude}
              data={data}
              zIndex={index + 100000}
            />
          );
        })}

      {!isRidePlayerOpen &&
        clusters.map((cluster: PointFeature<AnyProps>) => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const {
            cluster: isCluster,
            point_count: pointCount,
            tooltip,
            expansionZoom,
            isPrivate,
            hasAlarm,
            hasEvent,
            hasEventTypeDoor,
            hasEventTypeAdditional,
            hasEventTypeBluecoin,
          } = cluster.properties;
          if (isCluster) {
            // const clusterIndex: number = isWaypointsOpen ? index + 1 : index + 100000;
            // if (isWaypointsOpen) {
            //   clusterIndex = index + 1;
            // }
            return (
              <VehicleCluster
                isPrivate={isPrivate}
                hasAlarm={hasAlarm}
                hasEvent={hasEvent}
                hasEventTypeDoor={hasEventTypeDoor}
                hasEventTypeAdditional={hasEventTypeAdditional}
                hasEventTypeBluecoin={hasEventTypeBluecoin}
                items={pointCount}
                key={`cluster-${cluster.id}`}
                lat={latitude}
                lng={longitude}
                expansionZoom={expansionZoom}
                tooltipItems={tooltip}
                googleMap={props.googleMap}
                clusterId={String(cluster.id)}
                zIndex={String(onHoverClusterId) === String(cluster.id) ? 100001 : 100000}
                handleOnHover={handleOnHover}
              />
            );
          }

          return (
            <VehicleMarker
              key={`vehicle-${cluster.properties.vehicleId}`}
              lat={latitude}
              lng={longitude}
              vehicleId={cluster.properties.vehicleId}
              zIndex={getVehicleZIndex(cluster.properties.vehicleId)}
            />
          );
        })}
    </GoogleMapReact>
  );
}

export default React.memo(GoogleMapLayer);
